/**
 * Auth context for route guards and session state.
 * Security Checklist: client-side route authorization must be paired with server-side checks.
 * Session is persisted via HttpOnly cookie (brokers after profile complete; payers after client OTP); on mount we call GET /api/auth/me.
 * During signup (OTP verified but profile incomplete), only an in-memory Bearer token is used — no cookie —
 * so abandoning signup does not leave a persistent broker session.
 *
 * Dev only: when VITE_DEV_AUTO_AUTH is set, we auto-set the backend dev broker token only if /me failed.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getSessionToken, setSessionToken as storeSetSessionToken } from '@services/sessionStore';
import { getAuthMe, type AuthLoginContext, type AuthMeData, type AuthUserRole } from '@services/api';
import {
  registerBrokerApiUnauthorizedHandler,
  setHadAuthenticatedSession,
} from '@services/authSessionEvents';
import { BACKOFFICE_BASE, BACKOFFICE_LOGIN_PATH } from '@lib/backofficeRoutes';
import { clearPlatformStorage } from '@utils/resetStorage';

/** Backend seed uses this token for the first broker user. Dev only – do not use in production. */
const DEV_BROKER_TOKEN = 'dev-broker-session-token';

export interface AuthContextValue {
  /** True when the user has a valid session (server must enforce). */
  isAuthenticated: boolean;
  /** True until the first GET /api/auth/me has completed (prevents flicker). */
  authLoading: boolean;
  setAuthenticated: (value: boolean) => void;
  /** Token from last verify-otp (in-memory). Used by apiClient when cookie is not used. */
  sessionToken: string | null;
  /**
   * Set token and optionally mark authenticated. Call after successful verify-otp when token is returned.
   * profileCompleted: pass from OTP response so the app can gate routes before /me runs again.
   */
  setSessionToken: (
    token: string | null,
    authenticated?: boolean,
    options?: { profileCompleted?: boolean },
  ) => void;
  /** Clear token and set authenticated false. */
  clearSession: () => void;
  /** From /auth/me or OTP; null until first resolution. */
  profileCompleted: boolean | null;
  /** Re-fetch /auth/me (e.g. after POST /auth/complete sets HttpOnly cookie). Returns false if /me failed. */
  refreshAuth: () => Promise<boolean>;
  /** From GET /auth/me — `client` when session was created via payer link OTP; `broker` otherwise. */
  loginContext: AuthLoginContext | null;
  /** From GET /auth/me — `user` for brokers, dashboard roles for staff. Null until /me succeeds. */
  userRole: AuthUserRole | null;
  /** From GET /auth/me — the current user's id. Null until /me succeeds. */
  userId: string | null;
  /** From GET /auth/me — dynamic permissions for the current user. */
  userPermissions: string[];
  /** True when the broker has at least one COMPLETED NafathVerification. False until confirmed. */
  nafathVerified: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setAuthenticatedState] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [sessionToken, setSessionTokenState] = useState<string | null>(() => getSessionToken());
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(null);
  const [loginContext, setLoginContext] = useState<AuthLoginContext | null>(null);
  const [userRole, setUserRole] = useState<AuthUserRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [nafathVerified, setNafathVerified] = useState(false);

  const applyMeSuccess = useCallback((data: AuthMeData) => {
    storeSetSessionToken(null);
    setSessionTokenState(null);
    setAuthenticatedState(true);
    setProfileCompleted(Boolean(data.profileCompleted));
    setLoginContext(data.loginContext);
    const r = String(data.role ?? data.user?.role ?? 'user')
      .trim()
      .toLowerCase();
    setUserRole(r || 'user');
    setUserId(data.user?.id ?? null);
    setUserPermissions(data.permissions ?? data.user?.permissions ?? []);
    setNafathVerified(Boolean(data.user?.nafathVerified));
    setHadAuthenticatedSession(true);
  }, []);

  const applyMeFailure = useCallback(() => {
    storeSetSessionToken(null);
    setSessionTokenState(null);
    setAuthenticatedState(false);
    setProfileCompleted(null);
    setLoginContext(null);
    setUserRole(null);
    setUserId(null);
    setUserPermissions([]);
    setNafathVerified(false);
    setHadAuthenticatedSession(false);
  }, []);

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    try {
      const { data } = await getAuthMe();
      applyMeSuccess(data);
      return true;
    } catch {
      applyMeFailure();
      const devAutoAuth =
        typeof import.meta !== 'undefined' &&
        import.meta.env?.DEV === true &&
        import.meta.env?.VITE_DEV_AUTO_AUTH === 'true';
      if (devAutoAuth) {
        storeSetSessionToken(DEV_BROKER_TOKEN);
        setSessionTokenState(DEV_BROKER_TOKEN);
        setAuthenticatedState(true);
        setProfileCompleted(true);
        setLoginContext('broker');
        setUserRole('user');
        setNafathVerified(true);
        setHadAuthenticatedSession(true);
        return true;
      }
      return false;
    }
  }, [applyMeFailure, applyMeSuccess]);

  /** Re-sync permissions after tab focus so sidebar and PermissionGuard react to admin RBAC changes. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (!path.startsWith(BACKOFFICE_BASE)) return;
      void refreshAuth();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshAuth]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: set loading state before async auth check
    setAuthLoading(true);
    getAuthMe()
      .then(({ data }) => {
        if (!cancelled) applyMeSuccess(data);
      })
      .catch(() => {
        if (!cancelled) {
          applyMeFailure();
          const devAutoAuth =
            typeof import.meta !== 'undefined' &&
            import.meta.env?.DEV === true &&
            import.meta.env?.VITE_DEV_AUTO_AUTH === 'true';
          if (devAutoAuth) {
            storeSetSessionToken(DEV_BROKER_TOKEN);
            setSessionTokenState(DEV_BROKER_TOKEN);
            setAuthenticatedState(true);
            setProfileCompleted(true);
            setLoginContext('broker');
            setUserRole('user');
            setNafathVerified(true);
            setHadAuthenticatedSession(true);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setAuthLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyMeFailure, applyMeSuccess]);

  const setAuthenticated = useCallback((value: boolean) => {
    setAuthenticatedState(value);
  }, []);

  const setSessionToken = useCallback(
    (token: string | null, authenticated = true, options?: { profileCompleted?: boolean }) => {
      storeSetSessionToken(token);
      setSessionTokenState(token);
      if (!token) {
        setAuthenticatedState(false);
        setProfileCompleted(null);
        setLoginContext(null);
        setUserRole(null);
        setUserId(null);
        setHadAuthenticatedSession(false);
        return;
      }
      setAuthenticatedState(authenticated && !!token);
      if (authenticated && token) {
        setHadAuthenticatedSession(true);
      }
      if (options && typeof options.profileCompleted === 'boolean') {
        setProfileCompleted(options.profileCompleted);
      }
    },
    [],
  );

  const clearSession = useCallback(() => {
    storeSetSessionToken(null);
    setSessionTokenState(null);
    setAuthenticatedState(false);
    setProfileCompleted(null);
    setLoginContext(null);
    setUserRole(null);
    setUserId(null);
    setUserPermissions([]);
    setNafathVerified(false);
    setHadAuthenticatedSession(false);
  }, []);

  useEffect(() => {
    registerBrokerApiUnauthorizedHandler(() => {
      clearPlatformStorage();
      clearSession();
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        if (path.startsWith(BACKOFFICE_BASE)) {
          window.location.assign(BACKOFFICE_LOGIN_PATH);
        } else {
          window.location.assign('/');
        }
      }
    });
    return () => registerBrokerApiUnauthorizedHandler(null);
  }, [clearSession]);

  const value: AuthContextValue = {
    isAuthenticated,
    authLoading,
    setAuthenticated,
    sessionToken,
    setSessionToken,
    clearSession,
    profileCompleted,
    refreshAuth,
    loginContext,
    userRole,
    userId,
    userPermissions,
    nafathVerified,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
